// Copy for Marketing → Meta Ads → Product Sets (/admin/marketing/meta-ads).
// Albanian is the primary language for shop staff; English mirrors it.
// Merged into the global translations under the `metaAds` namespace.
//
// Wording rule: speak about products and ads, never about feeds, catalog IDs
// or labels — except inside the one-time "Connect in Meta" panel.

const tx = (sq, en) => ({ sq, en });

const metaAdsTranslations = {
  // ── Page ─────────────────────────────────────────────────
  eyebrowMarketing: tx("Marketingu", "Marketing"),
  eyebrowMetaAds: tx("Meta Ads", "Meta Ads"),
  eyebrowProductSet: tx("Grup produktesh", "Product set"),
  pageTitle: tx("Grupet e produkteve", "Product sets"),
  pageSubtitle: tx(
    "Zgjidh cilat produkte promovohen në Facebook dhe Instagram.",
    "Choose which products are promoted on Facebook and Instagram."
  ),
  createSet: tx("Krijo grup", "Create product set"),
  setsHeading: tx("Grupet", "Product sets"),
  loadingSets: tx("Duke ngarkuar grupet e produkteve…", "Loading product sets…"),
  setActionsFor: tx("Veprime për {name}", "Actions for {name}"),
  menuRename: tx("Ndrysho emrin", "Rename"),
  menuEditDetails: tx("Ndrysho emrin dhe përshkrimin", "Edit name and description"),
  menuDelete: tx("Fshi grupin", "Delete product set"),
  moreActions: tx("Më shumë veprime", "More actions"),
  // A selection is one model in one colour; its sizes come along.
  selectionWordOne: tx("model/ngjyrë", "product/colour"),
  selectionWordOther: tx("modele/ngjyra", "products/colours"),
  selectionCountOne: tx("{count} model/ngjyrë", "{count} product/colour"),
  selectionCountOther: tx("{count} modele/ngjyra", "{count} products/colours"),
  variantCountOne: tx("{count} masë në Meta", "{count} size in Meta"),
  variantCountOther: tx("{count} masa në Meta", "{count} sizes in Meta"),
  sizesList: tx("Masat: {sizes}", "Sizes: {sizes}"),
  updatedAgo: tx("Ndryshuar {time}", "Updated {time}"),
  justNow: tx("tani", "just now"),
  notReachingShortOne: tx("{count} model/ngjyrë nuk shfaqet në Meta", "{count} product/colour won't show on Meta"),
  notReachingShortOther: tx(
    "{count} modele/ngjyra nuk shfaqen në Meta",
    "{count} products/colours won't show on Meta"
  ),

  // ── Catalog status line ──────────────────────────────────
  catalogReadAt: tx("Meta e shkarkoi katalogun {time}", "Meta last downloaded your catalog {time}"),
  catalogNotReadYet: tx(
    "Ende nuk kemi parë Metën ta shkarkojë katalogun",
    "We haven't seen Meta download your catalog yet"
  ),
  catalogReadSchedule: tx(
    "Meta e kontrollon katalogun sipas orarit të vet.",
    "Meta checks your catalog on its own schedule."
  ),

  // ── Status (never "synced"/"sent": we only know when Meta downloaded the feed) ──
  stateEmpty: tx("Pa produkte", "No products yet"),
  stateEmptyHint: tx(
    "Shto produkte që ky grup të përdoret në reklama.",
    "Add products so this set can be used in ads."
  ),
  stateReady: tx("Gati për Meta", "Ready for Meta"),
  stateReadyHint: tx(
    "Ndryshimet janë ruajtur. Meta i merr herën tjetër që shkarkon katalogun.",
    "Your changes are saved. Meta picks them up the next time it downloads your catalog."
  ),
  stateDownloaded: tx("Shkarkuar nga Meta", "Downloaded by Meta"),
  stateDownloadedHint: tx(
    "Meta e shkarkoi katalogun pas ndryshimit të fundit. Kjo nuk konfirmon që Meta e ka përpunuar.",
    "Meta downloaded your catalog after your latest change. This doesn't confirm Meta has processed it."
  ),
  stateUnsaved: tx("Ndryshime të paruajtura", "Unsaved changes"),
  stateUnsavedHint: tx(
    "Ruaji ndryshimet që Meta t'i marrë.",
    "Save your changes so Meta can pick them up."
  ),

  // ── Empty list ───────────────────────────────────────────
  emptyTitle: tx("Krijo grupin e parë të produkteve", "Create your first product set"),
  emptyCopy: tx(
    "Një grup produktesh janë produktet që dëshiron të reklamosh — p.sh. “Dimër 2026” ose “Të rejat”. I zgjedh këtu dhe reklamat në Meta i përdorin vetë.",
    "A product set is the products you want to advertise — like “Winter 2026” or “New arrivals”. Pick them here and your Meta ads use them automatically."
  ),
  emptyStep1: tx("Jepi një emër", "Give it a name"),
  emptyStep2: tx("Zgjidh produktet", "Pick the products"),
  emptyStep3: tx("Reklamat në Meta i përdorin", "Your Meta ads use them"),

  // ── Create / edit ────────────────────────────────────────
  createTitle: tx("Grup i ri produktesh", "New product set"),
  createDescription: tx(
    "Në hapin tjetër zgjedh produktet që do të reklamohen.",
    "Next, you'll choose the products to advertise."
  ),
  editTitle: tx("Emri dhe përshkrimi", "Name and description"),
  editDescription: tx(
    "Ndryshimi i emrit nuk ndikon në reklamat ekzistuese.",
    "Renaming doesn't affect existing ads."
  ),
  nameLabel: tx("Emri", "Name"),
  namePlaceholder: tx("p.sh. Dimër 2026", "e.g. Winter 2026"),
  nameHint: tx("Një emër që e njeh e gjithë ekipa.", "A name everyone on the team will recognise."),
  nameHintEdit: tx("Lidhja me Meta mbetet e njëjtë.", "The connection to Meta stays the same."),
  nameTooShort: tx("Shkruaj të paktën 2 shkronja.", "Enter at least 2 characters."),
  descriptionLabel: tx("Përshkrimi", "Description"),
  descriptionPlaceholder: tx(
    "p.sh. Artikujt e rinj të dimrit për reklamat në Meta.",
    "e.g. New winter arrivals selected for Meta ads."
  ),
  descriptionHint: tx("Vetëm për ekipin — nuk shfaqet në reklama.", "For your team only — not shown in ads."),
  optional: tx("opsionale", "optional"),
  createAndChoose: tx("Krijo dhe zgjidh produktet", "Create and choose products"),
  saveDetails: tx("Ruaj", "Save"),
  close: tx("Mbyll", "Close"),
  cancel: tx("Anulo", "Cancel"),

  // ── Manage page ──────────────────────────────────────────
  backToSets: tx("Kthehu te grupet", "Back to product sets"),
  loadingSet: tx("Duke ngarkuar grupin…", "Loading product set…"),
  introTitle: tx("Zgjidh produktet për këtë grup", "Choose the products for this set"),
  introCopy: tx(
    "Kërko ose filtro, shëno produktet që do të reklamosh dhe pastaj ruaj. Çdo ngjyrë zgjidhet një herë — të gjitha masat e saj përfshihen vetë.",
    "Search or filter, tick the products you want to advertise, then save. Each colour is picked once — all of its sizes are included automatically."
  ),
  workspaceAria: tx("Zgjedhja e produkteve", "Product selection"),
  viewLabel: tx("Shfaq", "Show"),
  viewSelected: tx("Të zgjedhurat", "Selected"),
  viewAll: tx("Të gjitha produktet", "All products"),
  resultsSelectedAria: tx("Produktet e zgjedhura", "Selected products"),
  resultsAllAria: tx("Produktet e katalogut", "Catalog products"),
  selectedCountOne: tx("{count} i zgjedhur", "{count} selected"),
  selectedCountOther: tx("{count} të zgjedhura", "{count} selected"),
  addedCountOne: tx("{count} i shtuar", "{count} added"),
  addedCountOther: tx("{count} të shtuara", "{count} added"),
  removedCountOne: tx("{count} i hequr", "{count} removed"),
  removedCountOther: tx("{count} të hequra", "{count} removed"),
  selectMatchingOne: tx("Zgjidh {count} produkt", "Select {count} product"),
  selectMatchingOther: tx("Zgjidh të {count} produktet", "Select all {count} products"),
  deselectMatchingOne: tx("Hiq {count} produkt", "Deselect {count} product"),
  deselectMatchingOther: tx("Hiqi të {count} produktet", "Deselect all {count} products"),
  selectShownOne: tx("Zgjidh {count} të shfaqur", "Select {count} shown"),
  selectShownOther: tx("Zgjidh të {count} të shfaqurat", "Select all {count} shown"),
  deselectShownOne: tx("Hiq {count} të shfaqur", "Deselect {count} shown"),
  deselectShownOther: tx("Hiqi të {count} të shfaqurat", "Deselect all {count} shown"),
  clearSelection: tx("Pastro zgjedhjen", "Clear selection"),
  showingOf: tx("{shown} nga {total} produkte", "{shown} of {total} products"),
  loadMore: tx("Shfaq më shumë", "Show more"),
  loadingMore: tx("Duke ngarkuar…", "Loading…"),
  addProducts: tx("Shto produkte", "Add products"),
  setEmptyTitle: tx("Ky grup ende nuk ka produkte", "This set has no products yet"),
  setEmptyCopy: tx(
    "Shto produktet që dëshiron të reklamosh në Facebook dhe Instagram.",
    "Add the products you want to advertise on Facebook and Instagram."
  ),
  noResultsTitle: tx("Asnjë produkt nuk përputhet me filtrat", "No products match these filters"),
  noResultsForSearch: tx("Asnjë produkt për “{search}”", "No products for “{search}”"),
  noResultsCopy: tx(
    "Provo një fjalë tjetër, emrin ose kodin e produktit, ose hiq disa filtra.",
    "Try another word, the product name or code, or remove some filters."
  ),

  // ── Toolbar ──────────────────────────────────────────────
  searchLabel: tx("Kërko produkte", "Search products"),
  searchPlaceholder: tx("Kërko sipas emrit, kodit ose barkodit…", "Search by name, code or barcode…"),
  clearSearch: tx("Pastro kërkimin", "Clear search"),
  filters: tx("Filtrat", "Filters"),
  filtersTitle: tx("Filtro produktet", "Filter products"),
  filtersDescription: tx("Rezultatet përditësohen menjëherë.", "Results update as you go."),
  activeFilters: tx("Filtrat aktivë", "Active filters"),
  removeFilter: tx("Hiq filtrin {filter}", "Remove filter {filter}"),
  clearFilters: tx("Pastro filtrat", "Clear filters"),
  showResultsOne: tx("Shfaq {count} produkt", "Show {count} product"),
  showResultsOther: tx("Shfaq {count} produkte", "Show {count} products"),
  filterCategory: tx("Kategoria", "Category"),
  filterCategoryAll: tx("Të gjitha kategoritë", "All categories"),
  filterBrand: tx("Marka", "Brand"),
  filterBrandAll: tx("Të gjitha markat", "All brands"),
  filterFamily: tx("Lloji", "Department"),
  filterFamilyAll: tx("Të gjitha llojet", "All departments"),
  filterAdded: tx("Shtuar", "Added"),
  filterAddedAll: tx("Në çdo kohë", "Any time"),
  filterAddedWithin: tx("Në {days} ditët e fundit", "In the last {days} days"),
  filterStock: tx("Stoku", "Stock"),
  filterStockAll: tx("Çdo stok", "Any stock"),
  filterStock_in_stock: tx("Në stok", "In stock"),
  filterStock_low_stock: tx("Stok i ulët", "Low stock"),
  filterStock_out_of_stock: tx("Pa stok", "Out of stock"),
  filterSale: tx("Çmimi", "Price"),
  filterSaleAll: tx("Çdo çmim", "Any price"),
  filterSale_on_sale: tx("Në zbritje", "On sale"),
  filterSale_full_price: tx("Me çmim të plotë", "Full price"),
  filterMeta: tx("Në Meta", "On Meta"),
  filterMetaAll: tx("Të gjitha", "All"),
  filterMeta_reaching: tx("Shfaqen në Meta", "Shows on Meta"),
  filterMeta_not_reaching: tx("Nuk shfaqen në Meta", "Won't show on Meta"),
  sortLabel: tx("Rendit", "Sort"),
  sort_newest: tx("Më të rejat", "Newest first"),
  sort_oldest: tx("Më të vjetrat", "Oldest first"),
  sort_name: tx("Emri (A–Zh)", "Name (A–Z)"),
  sort_price_asc: tx("Çmimi: nga më i ulëti", "Price: low to high"),
  sort_price_desc: tx("Çmimi: nga më i larti", "Price: high to low"),
  sort_stock_desc: tx("Më shumë stok", "Most stock"),
  layoutLabel: tx("Pamja", "View"),
  layoutGrid: tx("Pamje me foto", "Grid view"),
  layoutList: tx("Pamje listë", "List view"),

  // ── Product tiles ────────────────────────────────────────
  saleBadge: tx("Zbritje", "Sale"),
  stockIn: tx("Në stok", "In stock"),
  stockInCount: tx("{count} në stok", "{count} in stock"),
  stockLow: tx("Stok i ulët", "Low stock"),
  stockLowCount: tx("Vetëm {count} në stok", "Only {count} left"),
  stockOut: tx("Pa stok", "Out of stock"),
  status_unpublished: tx("Jo në dyqan", "Not in store"),
  status_archived: tx("Arkivuar", "Archived"),
  reachHint: tx(
    "Mund ta mbash në grup, por nuk shfaqet në reklama derisa të rregullohet.",
    "You can keep it in the set, but it won't show in ads until this is fixed."
  ),
  issueNotPublished: tx("Nuk është publikuar në dyqan", "Not published in the store"),
  issueNotInCatalog: tx("Nuk është në katalogun Meta", "Not in the Meta catalog"),
  issueNoPrice: tx("Pa çmim", "No price"),
  issueNoImage: tx("Pa foto", "No photo"),
  issueNotPublishedCountOne: tx("{count} nuk është publikuar në dyqan", "{count} isn't published in the store"),
  issueNotPublishedCountOther: tx("{count} nuk janë publikuar në dyqan", "{count} aren't published in the store"),
  issueNotInCatalogCountOne: tx(
    "{count} nuk është përfshirë në katalogun Meta",
    "{count} isn't included in the Meta catalog"
  ),
  issueNotInCatalogCountOther: tx(
    "{count} nuk janë përfshirë në katalogun Meta",
    "{count} aren't included in the Meta catalog"
  ),
  issueNoPriceCountOne: tx("{count} nuk ka çmim", "{count} has no price"),
  issueNoPriceCountOther: tx("{count} nuk kanë çmim", "{count} have no price"),
  issueNoImageCountOne: tx("{count} nuk ka foto", "{count} has no photo"),
  issueNoImageCountOther: tx("{count} nuk kanë foto", "{count} have no photo"),

  // ── Attention ────────────────────────────────────────────
  attentionTitleOne: tx(
    "{count} model/ngjyrë i ruajtur nuk do të shfaqet në reklamat e Meta",
    "{count} saved product/colour won't appear in Meta ads"
  ),
  attentionTitleOther: tx(
    "{count} modele/ngjyra të ruajtura nuk do të shfaqen në reklamat e Meta",
    "{count} saved products/colours won't appear in Meta ads"
  ),
  attentionShow: tx("Shfaqi", "Show them"),
  includeInCatalogOne: tx("Përfshi {count} në katalogun Meta", "Include {count} in the Meta catalog"),
  includeInCatalogOther: tx("Përfshi {count} në katalogun Meta", "Include {count} in the Meta catalog"),
  includeSaveFirst: tx("Ruaj ndryshimet fillimisht.", "Save your changes first."),
  including: tx("Duke përfshirë…", "Including…"),

  // ── Save ─────────────────────────────────────────────────
  saveCountOne: tx("Ruaj {count} ndryshim", "Save {count} change"),
  saveCountOther: tx("Ruaj {count} ndryshime", "Save {count} changes"),
  saving: tx("Duke ruajtur…", "Saving…"),
  allSaved: tx("Të ruajtura", "Saved"),
  unsavedTitle: tx("Ndryshime të paruajtura", "Unsaved changes"),
  discard: tx("Hidh poshtë", "Discard"),
  leaveConfirm: tx(
    "Ke ndryshime të paruajtura në këtë grup. Të largohesh pa i ruajtur?",
    "You have unsaved changes in this product set. Leave without saving?"
  ),
  conflictTitle: tx("Dikush tjetër e ndryshoi këtë grup", "Someone else changed this product set"),
  conflictCopy: tx(
    "Grupi u ruajt nga dikush tjetër pasi e hape. Mund ta zëvendësosh me zgjedhjen tënde ose të ngarkosh versionin e tyre.",
    "It was saved by someone else after you opened it. You can replace it with your selection or load their version."
  ),
  conflictOverwrite: tx("Ruaj zgjedhjen time", "Save my selection"),
  conflictReload: tx("Hidh poshtë timen dhe ngarko të tyren", "Discard mine and load theirs"),
  unknownRemovedTitleOne: tx(
    "{count} produkt nuk ekziston më dhe u hoq nga zgjedhja",
    "{count} product no longer exists and was removed from your selection"
  ),
  unknownRemovedTitleOther: tx(
    "{count} produkte nuk ekzistojnë më dhe u hoqën nga zgjedhja",
    "{count} products no longer exist and were removed from your selection"
  ),
  unknownRemovedCopy: tx(
    "Asgjë nuk u ruajt ende. Shiko zgjedhjen dhe ruaje përsëri.",
    "Nothing was saved yet. Review your selection and save again."
  ),
  dismiss: tx("Mbyll", "Dismiss"),

  // ── Delete ───────────────────────────────────────────────
  deleteTitle: tx("Të fshihet “{name}”?", "Delete “{name}”?"),
  deleteCopyProducts: tx(
    "Produktet mbeten në katalog — fshihet vetëm grupi.",
    "Your products stay in the catalog — only the set is deleted."
  ),
  deleteCopyMeta: tx(
    "Reklamat në Meta që përdorin këtë grup do të ndalojnë së shfaquri këto produkte herën tjetër që Meta shkarkon katalogun. Ky veprim nuk kthehet.",
    "Meta ads that use this set will stop showing these products the next time Meta downloads your catalog. This can't be undone."
  ),
  deleteCopyUnsaved: tx(
    "Ndryshimet e paruajtura do të humbin gjithashtu.",
    "Your unsaved changes will be lost too."
  ),
  deleteConfirm: tx("Fshi grupin", "Delete product set"),
  deleting: tx("Duke fshirë…", "Deleting…"),

  // ── Meta setup (the one technical corner) ────────────────
  setupTitle: tx("Lidhe në Meta", "Connect in Meta"),
  setupSummary: tx(
    "Bëhet një herë, nga personi që menaxhon reklamat.",
    "One-time setup for whoever manages your ads."
  ),
  setupIntro: tx(
    "Në Meta Commerce Manager krijohet një herë një “product set” për këtë grup. Pas kësaj, produktet që zgjedh këtu kalojnë aty vetë.",
    "In Meta Commerce Manager, create a product set for this group once. After that, the products you choose here flow into it automatically."
  ),
  setupStep1: tx("Hap Commerce Manager → Katalogu → Product sets.", "Open Commerce Manager → Catalog → Product sets."),
  setupStep2: tx("Krijo një product set të ri me filtër.", "Create a new product set using a filter."),
  setupStep3: tx("Përdor këtë rregull:", "Use this rule:"),
  setupRuleField: tx("Internal label", "Internal label"),
  setupRuleOperator: tx("is any of", "is any of"),
  copy: tx("Kopjo", "Copy"),
  // Only the set's own label: "ads_primary" marks one colour per model, so
  // requiring it as well would hide every other colour a manager selected.
  setupOnlyThisRule: tx(
    "Përdor vetëm këtë rregull. Mos shto “{label}”: ai shënon vetëm një ngjyrë për çdo model, prandaj do të fshihte ngjyrat e tjera që ke zgjedhur.",
    "Use only this rule. Don't add “{label}”: it marks just one colour per model, so it would hide the other colours you selected."
  ),
  setupRenameNote: tx(
    "Ky kod nuk ndryshon kur e riemërton grupin.",
    "This code never changes, even if you rename the set."
  ),

  // ── Feedback ─────────────────────────────────────────────
  toastCreated: tx("Grupi u krijua. Tani zgjidh produktet.", "Product set created. Now choose its products."),
  toastUpdated: tx("Ndryshimet u ruajtën.", "Changes saved."),
  toastSaved: tx("Zgjedhja e produkteve u ruajt — {changes}.", "Product selection saved — {changes}."),
  toastDeleted: tx("“{name}” u fshi.", "“{name}” was deleted."),
  toastIncludedOne: tx("{count} produkt u përfshi në katalogun Meta.", "{count} product included in the Meta catalog."),
  toastIncludedOther: tx(
    "{count} produkte u përfshinë në katalogun Meta.",
    "{count} products included in the Meta catalog."
  ),
  toastReloaded: tx("U ngarkua versioni më i fundit.", "Loaded the latest version."),
  toastCopied: tx("U kopjua.", "Copied."),

  // ── Errors ───────────────────────────────────────────────
  retry: tx("Provo përsëri", "Try again"),
  loadSetsErrorTitle: tx("Grupet e produkteve nuk u ngarkuan", "Product sets couldn't be loaded"),
  loadSetsErrorCopy: tx("Kontrollo lidhjen dhe provo përsëri.", "Check your connection and try again."),
  loadSetErrorTitle: tx("Grupi nuk u ngarkua", "The product set couldn't be loaded"),
  loadSetErrorCopy: tx("Kontrollo lidhjen dhe provo përsëri.", "Check your connection and try again."),
  loadProductsErrorTitle: tx("Produktet nuk u ngarkuan", "Products couldn't be loaded"),
  loadProductsErrorCopy: tx(
    "Zgjedhja jote ruhet. Provo përsëri pas pak.",
    "Your selection is kept. Try again in a moment."
  ),
  notFoundTitle: tx("Ky grup nuk u gjet", "This product set wasn't found"),
  notFoundCopy: tx(
    "Mund të jetë fshirë. Kthehu te grupet e produkteve.",
    "It may have been deleted. Go back to your product sets."
  ),
  forbiddenTitle: tx("Nuk ke qasje në Marketing", "You don't have access to Marketing"),
  forbiddenCopy: tx(
    "Grupet e produkteve i menaxhojnë menaxherët, administratorët dhe pronarët.",
    "Product sets are managed by managers, admins and owners."
  ),
  errCreate: tx("Grupi nuk u krijua.", "Could not create the product set."),
  errUpdate: tx("Ndryshimet nuk u ruajtën.", "Could not save your changes."),
  errSave: tx("Zgjedhja e produkteve nuk u ruajt.", "Could not save the product selection."),
  errSaveKept: tx("Ndryshimet e tua janë ende këtu — provo përsëri.", "Your changes are still here — try again."),
  errDelete: tx("Grupi nuk u fshi.", "Could not delete the product set."),
  errInclude: tx(
    "Produktet nuk u përfshinë në katalogun Meta.",
    "Could not include the products in the Meta catalog."
  ),
  errNameTaken: tx("Ekziston tashmë një grup me këtë emër.", "A product set with this name already exists."),
  errNotFound: tx("Ky grup nuk ekziston më.", "This product set no longer exists."),
  errChanged: tx("Dikush tjetër e ndryshoi këtë grup.", "Someone else changed this product set."),
  errForbidden: tx("Nuk ke leje për këtë veprim.", "You don't have permission to do this."),
  errSession: tx("Seanca skadoi. Hyr përsëri.", "Your session expired. Please sign in again."),
  errOffline: tx("Kontrollo lidhjen me internetin.", "Check your internet connection."),
};

export default metaAdsTranslations;
